/**
 * ENQUIRIES. Priority 3 of the brief: every enquiry needs an owner, a next action and a date, so
 * "who has not been followed up" always has an answer. Office only; the server refuses a cleaner.
 *
 * TWO SHAPES OF ONE LIST. On a phone it is the stack of cards it always was, each carrying its own
 * "Work it" and its own conversion form, because a list read standing up cannot afford a panel that
 * covers the row it is about. From md up the same rows become a table you sort by a column, and the
 * row you press is worked in a panel on the right. `DataTable` draws both shapes from ONE array of
 * rows, so a column and a card can never drift apart, and the forms are drawn once at each width
 * rather than twice, which is also why their field ids carry the place they are drawn in.
 *
 * THE ORDER IS A CHOICE, AND IT OPENS NEWEST FIRST. Asked for by the owner on 2026-09-08: the
 * first question at a desk is usually "what has just come in", and the moment each one came in is
 * on every row and every card to answer it. Four orders are offered in the bar, at both widths
 * because a phone has no column headers: newest, oldest, what to work next, and by name. The
 * choice is remembered in this browser under `fk.enquiries.order` and nowhere else.
 *
 * "What to work next" IS THE SERVER'S OWN ORDER and this screen never invents one.
 * `api/src/seams/enquiries.ts` ranks the neglected first, and the four captions here are that
 * ladder read out loud: nobody has replied, the next action has passed, nobody owns it, being
 * worked, with booked and lost last when they are asked for. One piece of state holds the order,
 * so the select in the bar and the column headers can never say two different things about one
 * list. "Today" arrives with the list, so a next action is overdue by the same day the nightly
 * same-day-reply check reads, and never by the phone's clock.
 *
 * Three things are coral here, and each is a gap rather than a state: no reply yet, a next action
 * that is past or has no day, and nobody owning it. The state pill is never coral, because a state
 * is what happened and not what is owed.
 *
 * Booking an enquiry is converting it. The household, the person to ring and the home are made in
 * one batch and the enquiry is marked booked in the same write, so this screen offers that form
 * and never `booked` as a plain change of state. The server refuses the plain change and says so.
 *
 * A website lead is never typed in here. It arrives signed with the id the website minted, and the
 * server refuses `website_form` on a typed-in enquiry. The channel list on the form leaves it out.
 * A row that CARRIES one of those lead ids is the exception, and it is the whole exception: it may
 * be corrected away from the word and it may be given the word back, because the id is the proof.
 *
 * WHERE SHE CAME FROM IS ASKED FOUR WAYS ON THIS SCREEN, and each is the same block of fields.
 * It is corrected at any time from the panel and from the card, it is asked on any open enquiry a
 * call opened until somebody answers it, it is typed with the row on the way in, and the two
 * channels no wire can carry are typed in a form of their own. Priority 7 is knowing which channel
 * is worth money, and at twenty households the only honest read on that is what somebody typed
 * while she was still on the phone.
 *
 * NONE OF IT IS CORAL. A source nobody has corrected is not a gap anybody has to close.
 *
 * THE ROW THE FIND DOOR OPENS (ops #84): `/enquiries?open=<id>` is where the find page sends a
 * number or a website lead an enquiry holds, and the screen opens with that row pressed, its panel
 * beside the list from md up. A row the open list does not hold, a lead since lost, has the screen
 * ask once more for the booked and lost too, and open it there.
 * A household's forget links each enquiry that only shares a number of theirs here the same way,
 * for an owner to review by hand (ops #82), closed ones among them.
 *
 * THE PARTS ARE IN `enquiries/`, a module to each, named for what it draws, and this file is the
 * screen that puts them together. The rules a row is read by are in `enquiries/rows.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CHANNELS, ENQUIRY_STATES, type Channel, type EnquiryState } from '@fk/shared';
import {
  ApiError,
  listEnquiries,
  listUsers,
  type EnquiryList,
  type EnquiryRow,
  type Me,
  type OfficeUser,
} from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button } from '../components/Field.tsx';
import {
  DataTable,
  FilterBar,
  FilterSelect,
  FilterToggle,
  SortableHeader,
  SplitLayout,
  useBesideView,
  useWideView,
  type DataColumn,
  type SortDirection,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { shortInstant } from '../format.ts';
import { CHANNEL_LABELS, ENQUIRY_STATE_LABELS } from '../labels.ts';
import { officePeople } from '../people.ts';
import { openEnquiry } from '../source.ts';
import { EnquiryCard } from './enquiries/EnquiryCard.tsx';
import { LogAPlatformLead } from './enquiries/LogAPlatformLead.tsx';
import { NextActionWords, OwnerWord } from './enquiries/RowWords.tsx';
import { TypeOneIn } from './enquiries/TypeOneIn.tsx';
import { WorkPanel } from './enquiries/WorkPanel.tsx';
import {
  ORDERS,
  ORDER_LABELS,
  ORDER_SORTING,
  OWNER_FILTERS,
  OWNER_FILTER_LABELS,
  becameAHousehold,
  bucketGroups,
  orderOf,
  ownerMatches,
  rememberOrder,
  rememberedOrder,
  sortRows,
  stateTone,
  type Order,
  type OwnerFilter,
  type SortKey,
  type Sorting,
} from './enquiries/rows.ts';

/* ------------------------------------------------------------------ *
 * The screen.
 * ------------------------------------------------------------------ */

export function Enquiries({ me }: { me: Me }) {
  const wide = useWideView();
  const beside = useBesideView();
  const [search] = useSearchParams();
  /** The row the find door asked to open, read once at mount: a press elsewhere is the person's. */
  const [openAsked] = useState(() => search.get('open'));
  /** Whether the booked and lost were asked for on that row's account, which is asked once. */
  const widenedFor = useRef(false);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [stateFilter, setStateFilter] = useState<EnquiryState | ''>('');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter | ''>('');
  const [sourceFilter, setSourceFilter] = useState<Channel | ''>('');
  // Newest first unless this browser remembers being told otherwise. Read once, at mount, because
  // a value read on every render would fight the header somebody just pressed.
  const [sorting, setSorting] = useState<Sorting | null>(() => ORDER_SORTING[rememberedOrder()]);
  const [selectedId, setSelectedId] = useState<string | null>(openAsked);
  const [typing, setTyping] = useState(false);
  const [logging, setLogging] = useState(false);
  const [list, setList] = useState<EnquiryList | null>(null);
  const [people, setPeople] = useState<readonly OfficeUser[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  /**
   * The names in the office, asked for once and shared by every panel and card on the screen.
   * Three or four rows that do not change while somebody works a list, and one request rather than
   * one per open form: Workers Free allows fifty subrequests per invocation.
   *
   * A failure here is not a failure of this screen. The picker falls back to "Take it", which is
   * what ownership was before this read existed.
   *
   * AND THE FILTER IS THE SCREEN'S. That read answers with everybody who has a sign-in now, the
   * cleaners and the suspended accounts included, because the Crew panel that shuts a door has to
   * be shown the rows it acts on. An enquiry handed to one of them is refused by
   * `api/src/lib/office.ts` on the way in, so `officePeople` narrows the names where they arrive
   * and every picker below this shares the one predicate.
   */
  useEffect(() => {
    let live = true;
    listUsers()
      .then((answer) => {
        if (live) setPeople(officePeople(answer.users));
      })
      .catch(() => {
        // The list keeps the names the server already joined onto each row. Only the picker goes.
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    setError(null);
    setList(null);
    listEnquiries(includeClosed)
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The enquiries did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [includeClosed, attempt]);

  // The row the find door asked for, where the open list does not hold it: a lead since lost or
  // booked. Asked once, so an id nobody holds opens nothing and never asks again.
  useEffect(() => {
    if (openAsked === null || list === null || includeClosed || widenedFor.current) return;
    if (list.enquiries.some((row) => row.id === openAsked)) return;
    widenedFor.current = true;
    setIncludeClosed(true);
  }, [openAsked, list, includeClosed]);

  const changed = useCallback(
    (sentence: string) => {
      setSaid(sentence);
      reload();
    },
    [reload],
  );

  // Whichever control set it: the select and the headers are one piece of state, so pressing the
  // name column is remembered as "by name". An order none of the four names is not remembered,
  // and the last named choice is what the next visit opens in.
  const order = orderOf(sorting);
  useEffect(() => {
    if (order !== 'column') rememberOrder(order);
  }, [order]);

  const chooseOrder = (chosen: Order | '') => {
    if (chosen === '' || chosen === 'column') return;
    setSorting(ORDER_SORTING[chosen]);
  };

  /** The fifth option exists only while a column press has earned it, and choosing it does nothing. */
  const orderOptions: readonly Order[] = order === 'column' ? [...ORDERS, 'column'] : ORDERS;

  const rows = list?.enquiries ?? [];
  const today = list?.today ?? me.today;

  // The sources somebody can filter by are the ones in front of them, plus whatever is chosen, so
  // taking a filter off is always possible and choosing one never empties the list on purpose.
  const sourceOptions = CHANNELS.filter(
    (channel) => channel === sourceFilter || rows.some((row) => row.sourceChannel === channel),
  );
  const stateOptions = includeClosed ? ENQUIRY_STATES : ENQUIRY_STATES.filter(openEnquiry);

  const filtered = rows.filter(
    (row) =>
      (stateFilter === '' || row.state === stateFilter) &&
      (sourceFilter === '' || row.sourceChannel === sourceFilter) &&
      (ownerFilter === '' || ownerMatches(row, ownerFilter, me.userId)),
  );

  const groups = sorting
    ? [{ key: 'sorted', rows: sortRows(filtered, sorting, me.userId) }]
    : bucketGroups(filtered, today);

  // Resolved from everything loaded rather than from what the filters left, so narrowing the list
  // never closes the panel somebody is typing in.
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  const filtering = stateFilter !== '' || ownerFilter !== '' || sourceFilter !== '';
  const clearFilters = () => {
    setStateFilter('');
    setOwnerFilter('');
    setSourceFilter('');
  };

  const directionOf = (key: SortKey): SortDirection =>
    sorting?.key === key ? sorting.direction : 'none';

  /** Ascending, then descending, then back to the order the server sent. */
  const cycle = (key: SortKey) =>
    setSorting((current) =>
      current === null || current.key !== key
        ? { key, direction: 'ascending' }
        : current.direction === 'ascending'
          ? { key, direction: 'descending' }
          : null,
    );

  const sortable = (key: SortKey, label: string) => ({
    sort: directionOf(key),
    header: <SortableHeader label={label} direction={directionOf(key)} onSort={() => cycle(key)} />,
  });

  /*
   * WITH THE PANEL BESIDE IT, THE LIST MERGES TWO COLUMNS INTO ITS FIRST (ops #56). The panel
   * takes 420px of the row from xl, and the list kept all six columns: at the owner's laptop the
   * Owner column ran under the panel, and at 1366 the table scrolled in its frame. The town and the
   * source are the two a person reads least while working one enquiry, and the panel beside says
   * both of the one being worked, so while it is open they ride as a third line under the name
   * and the moment, and the coral words keep their room. Below xl the panel stands under the list,
   * the list keeps the page's width, and nothing is merged.
   */
  const merged = beside && selected !== null;

  const who: DataColumn<EnquiryRow> = {
    key: 'who',
    width: merged ? '184px' : '160px',
    ...sortable('who', 'Who asked'),
    cell: (row) => (
      // Two lines, and the second one is where the moment goes. A seventh column would have
      // taken it out of "Next action", which is the only column with no declared width and so
      // the one that pays for every other: the table is `table-fixed`, and it scrolls in its
      // frame only once "Next action" is down to its floor.
      // The coral gap words are the point of this screen and they are not the thing to squeeze.
      <button
        type="button"
        onClick={() => setSelectedId(row.id)}
        aria-pressed={row.id === selectedId}
        className="flex min-h-11 w-full flex-col justify-center text-left font-semibold desk:min-h-9"
      >
        {/* The size rides on the span, never on the button: styles.css sets 16px on every
            control outside a cascade layer, and an unlayered declaration beats the layered
            utility, so `text-sm` on the button itself would not render. The name wraps rather
            than being cut, since a clipped name is the one word the row is for (ops #56). */}
        <span className="min-w-0 text-sm break-words">{row.name}</span>
        {/* The moment it came in, in the business's zone: on a website lead the form's own
            submitted_at, on a call the call's occurred_at, on a typed one the moment it was
            typed. Written short here and in full on the card, which has the room. */}
        <span className="text-xs font-normal text-navy/65">{shortInstant(row.createdAt)}</span>
        {merged ? (
          <span className="text-xs font-normal break-words text-navy/65">
            {row.town ?? 'Town not said'}, {CHANNEL_LABELS[row.sourceChannel]}
          </span>
        ) : null}
      </button>
    ),
  };

  const whereFrom: readonly DataColumn<EnquiryRow>[] = merged
    ? []
    : [
        {
          key: 'town',
          width: '112px',
          cellClassName: 'text-navy/75',
          ...sortable('town', 'Town'),
          cell: (row) => row.town ?? <span className="text-navy/50">Not said</span>,
        },
        {
          key: 'source',
          width: '112px',
          cellClassName: 'text-navy/75',
          ...sortable('source', 'Source'),
          cell: (row) => CHANNEL_LABELS[row.sourceChannel],
        },
      ];

  const columns: readonly DataColumn<EnquiryRow>[] = [
    who,
    ...whereFrom,
    {
      key: 'state',
      width: '96px',
      ...sortable('state', 'State'),
      /*
       * A BOOKED ROW IS A DOOR TO THE HOUSEHOLD SHE BECAME. The list carries the client id now, so
       * the word `booked` stops being where the trail ends: pressing it opens her record. So is a
       * booking cancelled before any clean, whose `lost` is still her household's origin (ops #47,
       * step 4), as the card and the panel draw it. Every other state draws the pill on its own,
       * because there is nowhere else to go from it.
       */
      cell: (row) =>
        becameAHousehold(row) && row.clientId ? (
          <Link
            to={`/clients/${row.clientId}`}
            aria-label={`${row.name}, open their household`}
            className="flex min-h-11 desk:min-h-9 items-center"
          >
            <Pill tone={stateTone(row.state)}>{ENQUIRY_STATE_LABELS[row.state]}</Pill>
          </Link>
        ) : (
          <Pill tone={stateTone(row.state)}>{ENQUIRY_STATE_LABELS[row.state]}</Pill>
        ),
    },
    {
      key: 'owner',
      width: '80px',
      ...sortable('owner', 'Owner'),
      cell: (row) => <OwnerWord row={row} me={me} />,
    },
    {
      key: 'next',
      ...sortable('next', 'Next action'),
      cell: (row) => <NextActionWords row={row} today={today} />,
    },
  ];

  return (
    <div>
      <Link
        to="/clients"
        className="mb-3 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium text-navy/70 md:hidden"
      >
        Back to clients
      </Link>
      <ScreenTitle title="Enquiries" subtitle="Who has asked, and who has not been followed up." />

      <FilterBar
        /*
         * The tick travels with the two buttons (ops #56): it changes what is ASKED for rather
         * than narrowing what is shown, and alone on a second row it was the owner's point 4.
         */
        tail={
          <FilterToggle
            id="enquiries-include-closed"
            label="Include booked and lost"
            checked={includeClosed}
            onChange={(checked) => {
              setIncludeClosed(checked);
              if (!checked && (stateFilter === 'booked' || stateFilter === 'lost'))
                setStateFilter('');
            }}
          />
        }
        action={
          // Two ways in, and the second one is not a smaller version of the first. Typing an
          // enquiry in is a conversation somebody had; logging a platform lead is a row that has
          // to exist whether or not anybody ever spoke to her.
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              onClick={() => {
                setTyping((open) => !open);
                setLogging(false);
              }}
              full
            >
              Type one in
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setLogging((open) => !open);
                setTyping(false);
              }}
              full
            >
              Log a platform lead
            </Button>
          </div>
        }
      >
        {/*
         * The order is drawn at BOTH widths, unlike the three narrowing filters below it. A phone
         * has no column headers, so this select is the only way an order is chosen in a hand, and
         * it is the control the owner asked for on 2026-09-08.
         */}
        <FilterSelect
          id="enquiries-order"
          label="Order"
          value={order}
          onChange={chooseOrder}
          options={orderOptions}
          toLabel={(name) => ORDER_LABELS[name]}
          className="md:w-[180px]"
        />
        {/*
         * The three narrowing filters are the desk's, and they are not drawn on a phone. Three
         * stacked selects put 230px of chrome between the heading and the first card, which is the
         * phone paying for the desk, and the phone pays for nothing here. The list is short in a
         * hand, and the one filter that changes what is ASKED FOR stays at both widths.
         */}
        <div className="hidden md:contents">
          <FilterSelect
            id="enquiries-state"
            label="State"
            value={stateFilter}
            onChange={setStateFilter}
            options={stateOptions}
            toLabel={(state) => ENQUIRY_STATE_LABELS[state]}
            anyLabel={includeClosed ? 'Any state' : 'Anything open'}
            className="md:w-[150px]"
          />
          <FilterSelect
            id="enquiries-owner"
            label="Owner"
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={OWNER_FILTERS}
            toLabel={(owner) => OWNER_FILTER_LABELS[owner]}
            anyLabel="Anybody"
            /* Wider than the artboard's 140px, because "Somebody else" is a longer answer than
               the first name it draws and a clipped word in a filter is a filter nobody trusts. */
            className="md:w-[170px]"
          />
          <FilterSelect
            id="enquiries-source"
            label="Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={sourceOptions}
            toLabel={(channel) => CHANNEL_LABELS[channel]}
            anyLabel="Every source"
            className="md:w-[150px]"
          />
        </div>
      </FilterBar>

      {typing ? (
        <Card className="mb-4">
          <TypeOneIn
            onSaved={(sentence) => {
              setTyping(false);
              changed(sentence);
            }}
            onCancel={() => setTyping(false)}
          />
        </Card>
      ) : null}

      {logging ? (
        <Card className="mb-4">
          <LogAPlatformLead
            today={today}
            onSaved={(sentence) => changed(sentence)}
            onCancel={() => setLogging(false)}
          />
        </Card>
      ) : null}

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {error ? (
        <ErrorNotice title="The enquiries did not load" message={error.message} onRetry={reload} />
      ) : list === null ? (
        <Busy what="the enquiries" />
      ) : rows.length === 0 ? (
        <EmptyNotice title={includeClosed ? 'No enquiries yet' : 'Nobody is waiting'}>
          <p>
            {includeClosed
              ? 'One arrives from the website on its own, or is typed in above after a call.'
              : 'Every enquiry is booked or lost. Tick the box above to see them.'}
          </p>
        </EmptyNotice>
      ) : (
        <SplitLayout
          panel={
            wide && selected ? (
              // Keyed by the row, so pressing another name draws a panel with that row's own
              // boxes rather than the last one's typing under a new name.
              <WorkPanel
                key={selected.id}
                row={selected}
                today={today}
                me={me}
                people={people}
                onChanged={changed}
                onClose={() => setSelectedId(null)}
              />
            ) : undefined
          }
        >
          <div className="mb-2 hidden flex-wrap items-center justify-between gap-2 md:flex">
            <p className={`text-xs text-navy/60 ${MEASURE}`}>
              {sorting
                ? 'Sorted by what is chosen above, so the order the office should work them is set aside.'
                : 'In the order the office should work them. Choose another order above, or sort any column.'}
            </p>
            {sorting ? (
              <button
                type="button"
                onClick={() => setSorting(null)}
                className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-parchment px-4 font-medium"
              >
                <span className="text-sm">Back to the working order</span>
              </button>
            ) : null}
          </div>

          {filtered.length === 0 ? (
            <EmptyNotice title="Nothing matches those filters">
              <p>
                There are {rows.length} enquiries here, and none of them answer to what is chosen
                above.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-parchment px-4 font-medium"
              >
                <span className="text-sm">Show everything again</span>
              </button>
            </EmptyNotice>
          ) : (
            <DataTable
              label="Enquiries"
              groups={groups}
              columns={columns}
              rowKey={(row) => row.id}
              rowClassName={(row) =>
                row.id === selectedId
                  ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                  : ''
              }
              card={(row) => (
                <EnquiryCard
                  row={row}
                  today={today}
                  me={me}
                  people={people}
                  onChanged={changed}
                  actions={!wide}
                />
              )}
            />
          )}

          {/*
           * The only way a filter set at a desk can be taken off on a phone, so it is drawn at
           * both widths. The size sits on the span rather than the button, or the unlayered
           * 16px control rule in styles.css would put a 16px control mid-sentence in 12px copy.
           */}
          {filtering && filtered.length > 0 ? (
            <p className={`mt-2 text-xs text-navy/60 ${MEASURE}`}>
              {filtered.length} of {rows.length} shown.{' '}
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex min-h-11 desk:min-h-9 items-center font-medium text-navy underline"
              >
                <span className="text-xs">Show everything again</span>
              </button>
            </p>
          ) : null}
        </SplitLayout>
      )}
    </div>
  );
}
